/** @format */

import React, { useState } from 'react';
import { 
  Autocomplete, 
  TextField, 
  Checkbox, 
  FormControlLabel,
  IconButton,
  Tooltip,
  Box,
  Typography,
  Paper,
  Divider
} from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const TurnoverConfig = ({ addPackage, setAddPackage }) => {
  const billingTypes = [
    { name: 'Fixed Pricing', value: 'fixed' },
    { name: 'Turnover-Based Pricing', value: 'turnover' }
  ];

  const limitTypes = [
    { name: 'Monthly', value: 'monthly' },
    { name: 'Yearly', value: 'yearly' }
  ];

  const rateTypes = [
    { name: 'Percentage (%)', value: 'percentage' },
    { name: 'Fixed Amount', value: 'fixed' }
  ];

  const handleBillingTypeChange = (e, newValue) => {
    const isTurnover = newValue?.value === 'turnover';
    setAddPackage({
      ...addPackage,
      billingType: newValue?.value || 'fixed',
      turnoverEnabled: isTurnover
    });
  };

  const handleTurnoverChange = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setAddPackage({
      ...addPackage,
      [key]: value
    });
  };

  const handleAutocompleteChange = (key) => (e, newValue) => {
    setAddPackage({
      ...addPackage,
      [key]: newValue?.value || ''
    });
  };

  const addPricingTier = () => {
    const newTier = { from: 0, to: 0, price: 0 };
    setAddPackage({
      ...addPackage,
      pricingTiers: [...(addPackage.pricingTiers || []), newTier]
    });
  };

  const removePricingTier = (index) => {
    const updatedTiers = addPackage.pricingTiers.filter((_, i) => i !== index);
    setAddPackage({
      ...addPackage,
      pricingTiers: updatedTiers
    });
  };

  const updatePricingTier = (index, field, value) => {
    const updatedTiers = [...(addPackage.pricingTiers || [])];
    updatedTiers[index] = {
      ...updatedTiers[index],
      [field]: Number(value) || 0
    };
    setAddPackage({
      ...addPackage,
      pricingTiers: updatedTiers
    });
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3, backgroundColor: '#f8f9fa' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
          Billing Configuration
        </Typography>
        <Tooltip title="Configure how customers will be billed for this package">
          <InfoOutlinedIcon sx={{ ml: 1, color: '#7f8c8d', fontSize: 20 }} />
        </Tooltip>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Billing Type Selection */}
      <div className="global-single-input auto-complete" style={{ width: '100%', marginBottom: '20px' }}>
        <p style={{ fontWeight: 600, marginBottom: '8px' }}>Billing Type *</p>
        <Autocomplete
          sx={{ width: '100%' }}
          options={billingTypes}
          getOptionLabel={(option) => option?.name || ''}
          renderInput={(params) => <TextField {...params} placeholder="Select billing type" />}
          value={billingTypes.find(t => t.value === addPackage?.billingType) || billingTypes[0]}
          onChange={handleBillingTypeChange}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {addPackage?.billingType === 'turnover' 
            ? 'Customers will be billed based on their monthly/yearly turnover'
            : 'Customers will pay a fixed subscription price'}
        </Typography>
      </div>

      {/* Turnover Configuration - Only show if turnover billing is selected */}
      {addPackage?.billingType === 'turnover' && (
        <Box sx={{ mt: 3, p: 2, backgroundColor: 'white', borderRadius: 2, border: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#34495e' }}>
            Turnover Limit Configuration
          </Typography>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
            {/* Limit Type */}
            <div className="global-single-input auto-complete" style={{ width: '30%', minWidth: '200px' }}>
              <p style={{ fontWeight: 500 }}>Limit Type *</p>
              <Autocomplete
                sx={{ width: '100%' }}
                options={limitTypes}
                getOptionLabel={(option) => option?.name || ''}
                renderInput={(params) => <TextField {...params} />}
                value={limitTypes.find(t => t.value === addPackage?.turnoverLimitType) || limitTypes[0]}
                onChange={handleAutocompleteChange('turnoverLimitType')}
              />
            </div>

            {/* Turnover Limit Amount */}
            <div className="global-single-input" style={{ width: '30%', minWidth: '200px' }}>
              <p style={{ fontWeight: 500 }}>Turnover Limit Amount *</p>
              <input
                type="number"
                placeholder="e.g., 100000"
                value={addPackage?.turnoverLimitAmount || ''}
                onChange={handleTurnoverChange('turnoverLimitAmount')}
                style={{ width: '100%' }}
              />
              <Typography variant="caption" color="text.secondary">
                Maximum turnover per {addPackage?.turnoverLimitType === 'monthly' ? 'month' : 'year'}
              </Typography>
            </div>

            {/* Base Subscription Price */}
            <div className="global-single-input" style={{ width: '30%', minWidth: '200px' }}>
              <p style={{ fontWeight: 500 }}>Base Subscription Price *</p>
              <input
                type="number"
                placeholder="e.g., 5000"
                value={addPackage?.turnoverBasePrice || ''}
                onChange={handleTurnoverChange('turnoverBasePrice')}
                style={{ width: '100%' }}
              />
              <Typography variant="caption" color="text.secondary">
                Fixed {addPackage?.turnoverLimitType === 'monthly' ? 'monthly' : 'yearly'} fee
              </Typography>
            </div>
          </div>

          {/* Overage Charges */}
          <Box sx={{ mt: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={addPackage?.overageEnabled || false}
                  onChange={handleTurnoverChange('overageEnabled')}
                  sx={{ '& .MuiSvgIcon-root': { fontSize: 24 } }}
                />
              }
              label={
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Enable Overage Charges
                </Typography>
              }
            />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
              Charge customers when they exceed their turnover limit
            </Typography>

            {addPackage?.overageEnabled && (
              <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {/* Overage Rate Type */}
                <div className="global-single-input auto-complete" style={{ width: '45%', minWidth: '200px' }}>
                  <p style={{ fontWeight: 500 }}>Overage Rate Type *</p>
                  <Autocomplete
                    sx={{ width: '100%' }}
                    options={rateTypes}
                    getOptionLabel={(option) => option?.name || ''}
                    renderInput={(params) => <TextField {...params} />}
                    value={rateTypes.find(t => t.value === addPackage?.overageRateType) || rateTypes[0]}
                    onChange={handleAutocompleteChange('overageRateType')}
                  />
                </div>

                {/* Overage Rate */}
                <div className="global-single-input" style={{ width: '45%', minWidth: '200px' }}>
                  <p style={{ fontWeight: 500 }}>Overage Rate *</p>
                  <input
                    type="number"
                    placeholder={addPackage?.overageRateType === 'percentage' ? 'e.g., 5 (5%)' : 'e.g., 1000'}
                    value={addPackage?.overageRate || ''}
                    onChange={handleTurnoverChange('overageRate')}
                    style={{ width: '100%' }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {addPackage?.overageRateType === 'percentage' 
                      ? 'Percentage of excess turnover' 
                      : 'Fixed amount per unit over limit'}
                  </Typography>
                </div>
              </Box>
            )}
          </Box>

          {/* Pricing Tiers (Optional) */}
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#34495e' }}>
                  Pricing Tiers (Optional)
                </Typography>
                <Tooltip title="Define different prices for different turnover ranges">
                  <InfoOutlinedIcon sx={{ ml: 1, color: '#7f8c8d', fontSize: 18 }} />
                </Tooltip>
              </Box>
              <IconButton 
                onClick={addPricingTier}
                sx={{ 
                  color: '#27ae60', 
                  '&:hover': { backgroundColor: '#e8f5e9' }
                }}
              >
                <AddCircleOutlineIcon />
              </IconButton>
            </Box>

            {addPackage?.pricingTiers && addPackage.pricingTiers.length > 0 && (
              <Box sx={{ backgroundColor: 'white', p: 2, borderRadius: 1, border: '1px solid #e0e0e0' }}>
                {addPackage.pricingTiers.map((tier, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      display: 'flex', 
                      gap: 2, 
                      alignItems: 'center', 
                      mb: 2,
                      p: 1.5,
                      backgroundColor: '#f8f9fa',
                      borderRadius: 1
                    }}
                  >
                    <Typography variant="body2" sx={{ minWidth: '60px', fontWeight: 600 }}>
                      Tier {index + 1}
                    </Typography>
                    <div className="global-single-input" style={{ flex: 1 }}>
                      <p style={{ fontSize: '0.75rem', marginBottom: '4px' }}>From</p>
                      <input
                        type="number"
                        placeholder="0"
                        value={tier.from || ''}
                        onChange={(e) => updatePricingTier(index, 'from', e.target.value)}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div className="global-single-input" style={{ flex: 1 }}>
                      <p style={{ fontSize: '0.75rem', marginBottom: '4px' }}>To</p>
                      <input
                        type="number"
                        placeholder="50000"
                        value={tier.to || ''}
                        onChange={(e) => updatePricingTier(index, 'to', e.target.value)}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div className="global-single-input" style={{ flex: 1 }}>
                      <p style={{ fontSize: '0.75rem', marginBottom: '4px' }}>Price</p>
                      <input
                        type="number"
                        placeholder="5000"
                        value={tier.price || ''}
                        onChange={(e) => updatePricingTier(index, 'price', e.target.value)}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <IconButton 
                      onClick={() => removePricingTier(index)}
                      sx={{ color: '#e74c3c' }}
                    >
                      <DeleteOutlineOutlinedIcon />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            {(!addPackage?.pricingTiers || addPackage.pricingTiers.length === 0) && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No pricing tiers added. Click + to add tiers for different turnover ranges.
              </Typography>
            )}
          </Box>

          {/* Example Calculation */}
          {addPackage?.turnoverLimitAmount > 0 && addPackage?.turnoverBasePrice > 0 && (
            <Box sx={{ mt: 3, p: 2, backgroundColor: '#e8f5e9', borderRadius: 1, border: '1px solid #c8e6c9' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
                💡 Example Billing
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Base Price: ₹{Number(addPackage.turnoverBasePrice).toLocaleString()}/{addPackage.turnoverLimitType === 'monthly' ? 'month' : 'year'}
                <br />
                • Turnover Limit: ₹{Number(addPackage.turnoverLimitAmount).toLocaleString()}
                {addPackage.overageEnabled && (
                  <>
                    <br />
                    • Overage: {addPackage.overageRate}{addPackage.overageRateType === 'percentage' ? '%' : ' per unit'} on excess
                  </>
                )}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default TurnoverConfig;
